import React, { useEffect, useMemo, useState } from "react";


import avatar1 from "../assets/slider-3-author-1.webp";
import avatar2 from "../assets/slider-3-author-2.webp";
import avatar4 from "../assets/slider-3-author-4.webp";
/**
 * FavoriteTourSection
 * - 1:1 React conversion of the provided HTML (TailwindCSS)
 * - Fully mobile responsive, same layout/visuals
 * - Lightweight auto-fade slider for the hero image stack (no extra libs)
 * - Put images in /public (e.g. /assets/...) or swap the paths below
 * - Optional onBook() prop handles the "Book Now" click
 */
export default function FavoriteTourSection({ onBook }) {
  // Slides shown in the large image card
  const slides = useMemo(
    () => [
      {
        alt: "Udaipur",
        title: "Udaipur",
        location: "Rajasthan, India",
        reviewsLabel: "4.9 (2.1k reviews)",
        src: "/assets/g9-BHcJFrjD.webp",
      },
      {
        alt: "Hawa mahal - Jaipur",
        title: "Hawa Mahal - Jaipur",
        location: "Rajasthan, India",
        reviewsLabel: "4.9 (2.1k reviews)",
        src: "/assets/g1-BfUwq-qB.webp",
      },
      {
        alt: "Desert Safari- Jaisalmer",
        title: "Desert Safari - Jaisalmer",
        location: "Rajasthan, India",
        reviewsLabel: "4.9 (2.1k reviews)",
        src: "../assets/g4-CNkYhhUL.webp",
      },
      {
        alt: "Amer Fort- Jaipur",
        title: "Amer Fort - Jaipur",
        location: "Rajasthan, India",
        reviewsLabel: "4.9 (2.1k reviews)",
        src: "../assets/g2-Dl0JKKcO.webp",
      },
      {
        alt: "Jal Mahal - Jaipur",
        title: "Jal Mahal - Jaipur",
        location: "Rajasthan, India",
        reviewsLabel: "4.9 (2.1k reviews)",
        src: "../assets/g3-DvpWdpCy.webp",
      },
    ],
    []
  );

  const [index, setIndex] = useState(2); // start with Jaisalmer like the HTML (3rd slide visible)

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), 4500);
    return () => clearInterval(id);
  }, [slides.length]);

  const avatars = [avatar1, avatar2, avatar4];

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50/30 overflow-hidden">
      {/* Subtle pattern background */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23E91E63' fill-opacity='0.3'%3E%3Cpath d='M50 50c0-13.807-11.193-25-25-25s-25 11.193-25 25 11.193 25 25 25 25-11.193 25-25zm25 0c0-13.807-11.193-25-25-25s-25 11.193-25 25 11.193 25 25 25 25-11.193 25-25z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
          }}
        />
      </div>

      {/* Soft glows */}
      <div className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-br from-[#E91E63]/10 to-[#D32F2F]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-20 w-40 h-40 bg-gradient-to-br from-amber-400/10 to-orange-400/10 rounded-full blur-3xl" />

      <div className="relative flex min-h-screen items-center justify-center py-16 md:py-20 lg:py-24">
        <div className="container max-w-7xl grid gap-12 px-6 md:grid-cols-2 md:gap-16 lg:gap-20 xl:gap-24">
          {/* Left Column */}
          <div className="flex flex-col items-center justify-center space-y-8 text-center md:items-start md:text-left">
            {/* Social proof row */}
            <div className="flex items-center justify-center md:justify-start gap-3 transition-all duration-700 ease-out">
              <div className="flex -space-x-2 overflow-hidden">
                {avatars.map((src, i) => (
                  <div
                    key={i}
                    className="inline-block h-10 w-10 rounded-full ring-[3px] ring-white shadow-lg bg-gradient-to-br from-[#E91E63] to-[#D32F2F] flex items-center justify-center text-white text-xs font-bold"
                  >
                    <img
                      className="inline-block h-10 w-10 rounded-full ring-[3px] ring-white shadow-lg"
                      alt={`Traveler avatar ${i + 1}`}
                      width={40}
                      height={40}
                      loading="lazy"
                      src={src}
                    />
                  </div>
                ))}
              </div>

              <div className="text-gray-600">
                <span className="font-semibold text-gray-800">50K+ travelers choice!</span>
                <div className="flex items-center gap-1 mt-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <svg
                      key={i}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="w-4 h-4 fill-amber-400 text-amber-400"
                      aria-hidden="true"
                    >
                      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
                    </svg>
                  ))}
                  <span className="text-sm text-gray-600 ml-1">(4.9/5)</span>
                </div>
              </div>
            </div>

            {/* Heading */}
            <div className="space-y-4">
              <h2
                className="text-4xl md:text-5xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#E91E63] to-[#D32F2F] leading-tight transition-all duration-700 ease-out"
              >
                Most Favorite Tour Place
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-[#E91E63] to-[#D32F2F] mx-auto md:mx-0 rounded-full" />
            </div>

            {/* Subtext */}
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-xl">
              Discover the magnificent heritage and culture of Rajasthan through our carefully curated tour destinations with
              <span className="font-semibold text-[#E91E63]"> up-to 20% OFF</span>.
            </p>

            {/* Image Card (auto-fade slider) */}
            <div className="relative w-full max-w-lg h-[450px] overflow-hidden rounded-3xl shadow-2xl border border-white/20">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#E91E63]/20 via-[#E91E63]/20 to-[#D32F2F]/20 rounded-3xl blur-xl" />

              {slides.map((s, i) => (
                <div
                  key={s.src}
                  className={
                    "absolute inset-0 transition-all duration-1000 ease-in-out " +
                    (i === index ? "opacity-100 scale-100" : "opacity-0 scale-110")
                  }
                >
                  <img alt={s.alt} className="h-full w-full object-cover rounded-3xl" src={s.src} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent rounded-3xl" />

                  {/* Caption on active only (but safe to render always) */}
                  <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4 text-[#E91E63]" aria-hidden="true">
                        <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                      <span className="text-sm font-medium text-[#E91E63]">{s.location}</span>
                    </div>
                    <h3 className="text-2xl font-bold mb-2">{s.title}</h3>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }).map((_, i2) => (
                        <svg
                          key={i2}
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          className="w-4 h-4 fill-yellow-400 text-yellow-400"
                          aria-hidden="true"
                        >
                          <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
                        </svg>
                      ))}
                      <span className="ml-2 text-sm text-gray-300">{s.reviewsLabel}</span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Simple dots navigation (optional) */}
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    aria-label={`Go to slide ${i + 1}`}
                    className={
                      "h-2.5 w-2.5 rounded-full transition-all " +
                      (i === index ? "bg-white/90 scale-110" : "bg-white/40 hover:bg-white/60")
                    }
                  />
                ))}
              </div>
            </div>

            {/* Live-view badge */}
            <div className="flex items-center gap-2 bg-[#E0F7FA] border border-[#00838F]/20 rounded-full px-4 py-2 shadow-sm">
              <div className="w-3 h-3 bg-[#00838F] rounded-full animate-pulse" />
              <span className="text-sm text-[#00838F] font-bold">7 people are checking out right now</span>
            </div>
          </div>

          {/* Right Column */}
          <div className="flex flex-col items-center justify-center space-y-8 text-center md:items-start md:text-left">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-full px-4 py-2 backdrop-blur-sm">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4 text-emerald-600" aria-hidden="true">
                <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                <path d="m9 11 3 3L22 4"></path>
              </svg>
              <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest">Why We Best</span>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4 text-emerald-600" aria-hidden="true">
                <path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526"></path>
                <circle cx="12" cy="8" r="6"></circle>
              </svg>
            </div>

            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#E91E63] to-[#D32F2F] leading-tight">
                Why Choose Us
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-[#E91E63] to-[#D32F2F] mx-auto lg:mx-0 rounded-full" />
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-xl">
                Experience excellence with our government-approved, trusted travel services designed for unforgettable journeys.
              </p>
            </div>

            {/* Feature grid */}
            <div className="grid grid-cols-2 gap-6 w-full max-w-2xl">
              {[
                {
                  bg: "from-emerald-500 to-teal-600",
                  panel: "bg-emerald-50 border-emerald-200",
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-8 w-8 text-white relative z-10" aria-hidden="true">
                      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
                      <path d="m9 12 2 2 4-4"></path>
                    </svg>
                  ),
                  text: "Approved By Ministry Of Tourism, Govt. Of India",
                },
                {
                  bg: "from-blue-500 to-indigo-600",
                  panel: "bg-blue-50 border-blue-200",
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-8 w-8 text-white relative z-10" aria-hidden="true">
                      <path d="M12 7v14"></path>
                      <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"></path>
                    </svg>
                  ),
                  text: "Trusted Name In Rajasthan Tour Packages",
                },
                {
                  bg: "from-purple-500 to-violet-600",
                  panel: "bg-purple-50 border-purple-200",
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-8 w-8 text-white relative z-10" aria-hidden="true">
                      <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                      <rect width="20" height="14" x="2" y="6" rx="2"></rect>
                    </svg>
                  ),
                  text: "Best B2B Service Provider",
                },
                {
                  bg: "from-amber-500 to-orange-600",
                  panel: "bg-amber-50 border-amber-200",
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-8 w-8 text-white relative z-10" aria-hidden="true">
                      <line x1="12" x2="12" y1="2" y2="22"></line>
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                    </svg>
                  ),
                  text: "Value For Money Packages",
                },
                {
                  bg: "from-rose-500 to-pink-600",
                  panel: "bg-rose-50 border-rose-200",
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-8 w-8 text-white relative z-10" aria-hidden="true">
                      <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                  ),
                  text: "Customized Solutions",
                },
                {
                  bg: "from-cyan-500 to-blue-600",
                  panel: "bg-cyan-50 border-cyan-200",
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-8 w-8 text-white relative z-10" aria-hidden="true">
                      <path d="M4 16v-2.38C4 11.5 2.97 10.5 3 8c.03-2.72 1.49-6 4.5-6C9.37 2 10 3.8 10 5.5c0 3.11-2 5.66-2 8.68V16a2 2 0 1 1-4 0Z"></path>
                      <path d="M20 20v-2.38c0-2.12 1.03-3.12 1-5.62-.03-2.72-1.49-6-4.5-6C14.63 6 14 7.8 14 9.5c0 3.11 2 5.66 2 8.68V20a2 2 0 1 0 4 0Z"></path>
                      <path d="M16 17h4"></path>
                      <path d="M4 13h4"></path>
                    </svg>
                  ),
                  text: "Personalized Services",
                },
              ].map((feat, i) => (
                <div
                  key={i}
                  className={
                    `group flex flex-col items-center space-y-4 p-6 rounded-2xl border transition-all duration-700 ease-out hover:scale-105 hover:shadow-xl ${feat.panel}`
                  }
                >
                  <div className={`relative flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${feat.bg} shadow-lg group-hover:shadow-xl transition-all duration-300 overflow-hidden`}>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                    {feat.icon}
                    <div className="absolute top-[-0.5px] right-[-0.5px] w-5 h-5 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg opacity-100">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-3 h-3 text-white" aria-hidden="true">
                        <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                        <path d="m9 11 3 3L22 4"></path>
                      </svg>
                    </div>
                  </div>
                  <p className="text-center text-sm font-semibold text-slate-700 leading-tight group-hover:text-slate-900 transition-colors duration-300">
                    {feat.text}
                  </p>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
              <button
                onClick={() => (onBook ? onBook() : null)}
                className="group flex-1 relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#E91E63] to-[#D32F2F] py-4 px-6 text-lg font-bold text-white shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-100 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="relative">Book Now</span>
              </button>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4 text-emerald-500" aria-hidden="true">
                  <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                  <path d="m9 11 3 3L22 4"></path>
                </svg>
                <span className="font-medium">50K+ Happy Travelers</span>
              </div>
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4 text-yellow-500" aria-hidden="true">
                  <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
                </svg>
                <span className="font-medium">4.9/5 Rating</span>
              </div>
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4 text-blue-500" aria-hidden="true">
                  <path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526"></path>
                  <circle cx="12" cy="8" r="6"></circle>
                </svg>
                <span className="font-medium">Award Winning</span>
              </div>
            </div>

            {/* Stats */}
            <div className="flex gap-4 justify-center md:justify-start">
              {[
                { value: "50K+", label: "Happy Travelers" },
                { value: "15+", label: "Destinations" },
                { value: "4.9", label: "Rating" },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-xl px-4 py-3 shadow-xl border border-gray-100">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#E91E63]">{s.value}</div>
                    <div className="text-xs text-gray-600">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
